import { GBP_LIBRARY_SKIP_REASON_LABELS, type GbpLibrarySkipReason } from '@repo/shared/google-ranking';
import type { GbpPhoto, GetMarketingGoogleRankingPhotosTab } from '@/api/generated/schemas';
import { formatSalonMonthDay, photoCategoryLabel, photoSubjectChipLabel } from '../posts/google-post-format';
import { isSalon, type BusinessKindCopy } from '../use-business-kind-copy';

/** The tabs drawn as pills. Removed photos sit behind a small link under Ready instead. */
export const PHOTO_LIBRARY_PILL_TABS = ['ready', 'google', 'skipped'] as const satisfies readonly GetMarketingGoogleRankingPhotosTab[];
export type PhotoLibraryPillTab = (typeof PHOTO_LIBRARY_PILL_TABS)[number];

export const PHOTO_LIBRARY_TAB_LABEL: Record<PhotoLibraryPillTab, string> = {
  ready: 'Ready',
  google: 'On Google',
  skipped: 'Skipped',
};

/** `?tab=` of the library route — anything unknown is Ready. */
export function toPhotoLibraryPillTab(value: unknown): PhotoLibraryPillTab {
  return (PHOTO_LIBRARY_PILL_TABS as readonly unknown[]).includes(value) ? (value as PhotoLibraryPillTab) : 'ready';
}

/** Picked for a post that is not live yet: the photo autopilot never sends it as a profile photo. */
export function isPhotoHeldByPost(photo: Pick<GbpPhoto, 'plannedPostAt' | 'inFailedPost'>): boolean {
  return photo.plannedPostAt !== null || photo.inFailedPost;
}

/** The small grid preview when the photo has one (older photos don't), else the full photo. */
export function photoPreviewUrl(photo: Pick<GbpPhoto, 'thumbUrl' | 'imageUrl'>): string {
  return photo.thumbUrl ?? photo.imageUrl;
}

/** What the photo shows (the check's subject), else the Google category it goes under. */
export function photoSubjectLabel(photo: Pick<GbpPhoto, 'subject' | 'category'>, kind: BusinessKindCopy): string {
  return photo.subject ? photoSubjectChipLabel(photo.subject, kind) : photoCategoryLabel(photo.category, kind);
}

/** Why the check skipped a photo; off-topic reads "Not a salon photo" for salons, "Not about your restaurant"… */
export function photoSkipReasonLabel(reason: GbpLibrarySkipReason, kind: BusinessKindCopy): string {
  if (reason === 'NOT_SALON' && !isSalon(kind)) return `Not about your ${kind.noun}`;
  return GBP_LIBRARY_SKIP_REASON_LABELS[reason];
}

/**
 * Title + line under a library tile:
 * - skipped: why the check left it out · when it came in;
 * - on Google: when it went up (or the post it is in), on its way, or why it didn't go out;
 * - ready / removed: what it shows · when it was added (or the not-yet-live post it is in).
 */
export function photoLibraryTileText(
  photo: GbpPhoto,
  timezone: string | undefined,
  kind: BusinessKindCopy
): { title: string; meta: string } {
  const day = (iso: string) => formatSalonMonthDay(iso, timezone);
  const shows = () => photoSubjectLabel(photo, kind);
  switch (photo.status) {
    case 'SKIPPED':
      return {
        title: photo.skipReason ? photoSkipReasonLabel(photo.skipReason, kind) : 'Skipped',
        meta: day(photo.createdAt),
      };
    case 'SENT':
      return { title: shows(), meta: `On Google ${day(photo.sentAt ?? photo.createdAt)}` };
    case 'QUEUED':
    case 'SENDING':
      return { title: shows(), meta: 'On its way to Google' };
    case 'FAILED':
      return { title: shows(), meta: photo.error ?? 'Didn’t go out' };
    default:
      // A ready photo shown in a live Google post is listed under On Google.
      if (photo.inPostAt) return { title: shows(), meta: `In post ${day(photo.inPostAt)}` };
      // Picked for a post that is not live yet: listed under Ready until the post goes up.
      if (photo.plannedPostAt) return { title: shows(), meta: `In a post going out ${day(photo.plannedPostAt)}` };
      if (photo.inFailedPost) return { title: shows(), meta: 'In a post that didn’t go out' };
      return { title: shows(), meta: `Added ${day(photo.createdAt)}` };
  }
}

const WEEKS_PER_MONTH = 52 / 12;

/** "enough for about 4 months" / "about 3 weeks" — how long the Ready photos last at 2 a week. */
export function photosLastLabel(ready: number, weeks: number): string {
  if (ready === 0) return 'autopilot pauses photos until you add some';
  if (weeks < 1) return 'enough for the next few days';
  if (weeks < 8) return `enough for about ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  return `enough for about ${Math.round(weeks / WEEKS_PER_MONTH)} months`;
}
